import { secondaryTextClassName } from '@monecolier/ui';
import type { AttendanceWeeklySummaryDay } from '@monecolier/api-client';

/**
 * ATT-04 — graphique en barres Lundi-Vendredi de la carte "Présence — cette
 * semaine" (accueil admin). Aucun composant de graphique n'existe dans
 * `packages/ui` (vérifié à l'étape préalable, même constat que UX-44/
 * `SexBreakdownDonut`) — construit ICI, en local, même précédent : un SEUL
 * consommateur réel à ce jour (`AttendanceWeeklySummaryPanel`), à extraire
 * vers `packages/ui` seulement le jour où un second consommateur réel
 * apparaît. Barres en `<div>` (hauteur en `%` via `style`, pas de SVG ici —
 * un simple histogramme ne le justifie pas), même discipline "zéro
 * dépendance de charting" que `SexBreakdownDonut`.
 */
const CHART_HEIGHT = 96;

const DAY_LABELS: Record<AttendanceWeeklySummaryDay['dayOfWeek'], string> = {
  MONDAY: 'Lun',
  TUESDAY: 'Mar',
  WEDNESDAY: 'Mer',
  THURSDAY: 'Jeu',
  FRIDAY: 'Ven',
};

interface AttendanceWeeklyBarChartProps {
  days: AttendanceWeeklySummaryDay[];
}

export function AttendanceWeeklyBarChart({ days }: AttendanceWeeklyBarChartProps) {
  return (
    <div className="flex items-end justify-between gap-3" data-testid="attendance-weekly-bar-chart" style={{ height: CHART_HEIGHT + 40 }}>
      {days.map((day) => {
        const hasData = day.totalCount > 0;
        const percent = hasData ? Math.round((day.rate ?? 0) * 100) : null;
        const barHeight = hasData ? Math.max(4, Math.round((day.rate ?? 0) * CHART_HEIGHT)) : 0;

        return (
          <div
            key={day.dayOfWeek}
            className="flex flex-1 flex-col items-center justify-end gap-1"
            data-testid={`attendance-weekly-bar-${day.dayOfWeek}`}
          >
            <span className={`text-xs font-medium ${hasData ? 'text-gray-700' : secondaryTextClassName}`}>
              {percent === null ? '—' : `${percent}%`}
            </span>
            <div className="flex w-full items-end justify-center rounded-t bg-gray-100" style={{ height: CHART_HEIGHT }}>
              {hasData ? (
                <div
                  className="w-full rounded-t bg-brand-500"
                  style={{ height: barHeight }}
                  aria-hidden="true"
                  data-testid={`attendance-weekly-bar-fill-${day.dayOfWeek}`}
                />
              ) : null}
            </div>
            <span className={`text-xs font-semibold ${secondaryTextClassName}`}>{DAY_LABELS[day.dayOfWeek]}</span>
          </div>
        );
      })}
    </div>
  );
}
